import {clsx} from 'clsx'
import {announce} from '@github-ui/aria-live'
import {useEffect, useId, useRef} from 'react'

import styles from './MultiColorProgressBar.module.css'

export type ProgressSegmentColor = 'success' | 'neutral' | 'attention' | 'danger'

export type ProgressSegment = {
  color: ProgressSegmentColor
  dividerBefore?: boolean
  id: string
  label: string
  percentage: number
  testId?: string
}

export type MultiColorProgressBarProps = {
  'aria-label': string
  'aria-valuetext'?: string
  announceAvailabilityChanges?: boolean
  blockingSummary?: string
  clearedBlockingSummary?: string
  max: number
  miniStatusSpacing?: boolean
  semantics?: 'breakdown' | 'progress'
  state: {kind: 'available'; segments: readonly ProgressSegment[]} | {kind: 'unavailable'; label: string}
  value: number
}

export function MultiColorProgressBar({
  'aria-label': ariaLabel,
  'aria-valuetext': ariaValueText,
  announceAvailabilityChanges = true,
  blockingSummary,
  clearedBlockingSummary,
  max,
  miniStatusSpacing = false,
  semantics = 'progress',
  state,
  value,
}: MultiColorProgressBarProps) {
  const blockingSummaryId = useId()
  const valueText =
    state.kind === 'available'
      ? (ariaValueText ?? state.segments.map(segment => segment.label).join(', '))
      : state.label
  const announcement = [
    ariaLabel,
    blockingSummary ?? (state.kind === 'available' ? clearedBlockingSummary : undefined),
    valueText,
  ]
    .filter(Boolean)
    .join('. ')
  const previousAnnouncementRef = useRef({message: announcement, kind: state.kind})

  useEffect(() => {
    const previous = previousAnnouncementRef.current
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- Status data refreshes independently of user interaction.
    if (previous.message !== announcement || previous.kind !== state.kind) {
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- A consumer may already announce availability transitions.
      if (announceAvailabilityChanges || previous.kind === state.kind) announce(announcement)
      previousAnnouncementRef.current = {message: announcement, kind: state.kind}
    }
  }, [announceAvailabilityChanges, announcement, state.kind])

  if (state.kind === 'unavailable') {
    return (
      <div
        className={clsx(styles.Progress, miniStatusSpacing && styles.MiniStatusSpacing)}
        data-testid="multicolor-progress-unavailable"
      >
        <div className={styles.Unavailable} aria-hidden="true" />
        <p className={styles.BlockingSummary}>{blockingSummary ? `${blockingSummary}. ${state.label}` : state.label}</p>
      </div>
    )
  }

  validateSegments(state.segments)

  const progress = normalizeProgress(value, max)
  const breakdownLabel = [ariaLabel, valueText].join('. ')

  return (
    <div
      className={clsx(styles.Progress, miniStatusSpacing && styles.MiniStatusSpacing)}
      data-testid="multicolor-progress"
    >
      {semantics === 'progress' ? (
        <span
          aria-describedby={blockingSummary ? blockingSummaryId : undefined}
          aria-label={ariaLabel}
          aria-valuemax={100}
          aria-valuemin={0}
          aria-valuenow={progress}
          aria-valuetext={valueText}
          className="sr-only"
          role="progressbar"
        />
      ) : (
        <span
          aria-describedby={blockingSummary ? blockingSummaryId : undefined}
          aria-label={breakdownLabel}
          className="sr-only"
          role="img"
        />
      )}
      <div className={styles.Bar} data-testid="multicolor-progress-bar">
        {state.segments.map((segment, index) => {
          const previousSegment = state.segments[index - 1]
          const showSeparator = previousSegment && (segment.dividerBefore || previousSegment.color !== segment.color)

          return (
            <span
              aria-hidden="true"
              className={clsx(styles.Segment, toneClass(segment.color))}
              data-testid={segment.testId}
              key={segment.id}
              style={{width: `${segment.percentage}%`}}
            >
              {showSeparator ? (
                <span aria-hidden="true" className={styles.Separator} data-testid="multicolor-progress-separator" />
              ) : null}
            </span>
          )
        })}
      </div>
      {blockingSummary ? (
        <p className={styles.BlockingSummary} data-testid="mini-status-blocking-summary" id={blockingSummaryId}>
          {blockingSummary}
        </p>
      ) : null}
    </div>
  )
}

function normalizeProgress(value: number, max: number) {
  if (!Number.isFinite(value) || !Number.isFinite(max) || max <= 0) return 0
  return Math.min(100, Math.max(0, (value / max) * 100))
}

function toneClass(color: ProgressSegmentColor) {
  switch (color) {
    case 'success':
      return styles.ToneSuccess
    case 'neutral':
      return styles.ToneNeutral
    case 'attention':
      return styles.ToneAttention
    case 'danger':
      return styles.ToneDanger
  }
}

function validateSegments(segments: readonly ProgressSegment[]) {
  if (process.env.NODE_ENV === 'production') return

  if (
    segments.length === 0 ||
    segments.some(segment => !Number.isFinite(segment.percentage) || segment.percentage <= 0)
  ) {
    throw new Error('Available multicolor progress requires positive segments')
  }

  const total = segments.reduce((sum, segment) => sum + segment.percentage, 0)
  if (Math.abs(total - 100) > 0.1) {
    throw new Error(`Multicolor progress segments must total 100; received ${total}`)
  }
}
